import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import type { ParentIssueType } from '@risksmart-app/domain/src/types/consts';
import type { GetIssuesByParentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BadgeList from 'src/components/badge-list';
import SimpleRatingBadge from 'src/components/simple-rating-badge';
import { getAllOwnersCellValue } from 'src/rbac/contributorHelper';

import Link from '@/components/link';
import { toLocalDate } from '@/utils/dateUtils';
import { IssueTypeMapping } from '@/utils/issueVariantUtils';
import type { UseGetTablePropsOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';

type IssueFields = GetIssuesByParentIdQuery['issue'][0];

export type IssueTableFields = IssueFields & {
  TargetCloseDate: null | string;
  Severity: null | number;
  Status: null | string;
};

const useGetFieldConfig = (
  type: ParentIssueType
): TableFields<IssueTableFields> => {
  const issueMapping = IssueTypeMapping[type];
  const { getByValue: getSeverityByValue } = useRating('severity');
  const { getByValue: getStatusByValue } = useRating('issue_assessment_status');
  const { t: st } = useTranslation(['common'], {
    keyPrefix: `${issueMapping.taxonomy}.columns`,
  });
  const { t } = useTranslation(['common'], { keyPrefix: 'columns' });

  return useMemo<TableFields<IssueTableFields>>(
    () => ({
      Title: {
        header: st('title'),
        isRowHeader: true,
        cell: (item) => (
          <Link variant={'secondary'} href={`${item.Id}`}>
            {item.Title}
          </Link>
        ),
      },
      owners: {
        header: t('owners'),
        cell: (item) => (
          <BadgeList
            badges={getAllOwnersCellValue(item).map(
              (ownersCellValue) => ownersCellValue.label
            )}
          />
        ),
      },
      Severity: {
        header: st('severity'),
        cell: (item) => (
          <SimpleRatingBadge rating={getSeverityByValue(item.Severity)} />
        ),
      },
      Status: {
        header: st('status'),
        cell: (item) => (
          <SimpleRatingBadge rating={getStatusByValue(item.Status)} />
        ),
      },
      DateIdentified: {
        header: st('raised'),
        cell: (item) => toLocalDate(item.DateIdentified) || '-',
      },
      TargetCloseDate: {
        header: st('target_close_date'),
        cell: (item) =>
          item.TargetCloseDate ? toLocalDate(item.TargetCloseDate) || '-' : '-',
      },
      tags: {
        header: t('tags'),
        cell: (item) => (
          <BadgeList
            badges={item.tags
              .map((tag) => tag.type?.Name)
              .filter((e) => e != null)}
          />
        ),
      },
    }),
    [st, t, getSeverityByValue, getStatusByValue]
  );
};

const useGetIssuesTabProps = (
  type: ParentIssueType,
  records: IssueTableFields[] | undefined
): UseGetTablePropsOptions<IssueTableFields> => {
  const issueMapping = IssueTypeMapping[type];
  const { t: st } = useTranslation(['common'], {
    keyPrefix: issueMapping.taxonomy,
  });
  const fields = useGetFieldConfig(type);

  return useMemo(
    () => ({
      tableId: 'issuesTab',
      data: records,
      // Surfaces the issue variant's own custom fields as toggleable columns.
      customAttributeFormIds: [type],
      entityLabel: st('entity_name'),
      emptyCollectionAction: <></>,
      // Shared across issue variants — standard columns are identical; variant
      // custom-field ids that don't apply are simply ignored by the prefs store.
      preferencesStorageKey: 'IssuesSubTable-PreferencesV1',
      enableFiltering: false,
      defaultSortingState: {
        sortingColumn: 'DateIdentified',
        sortingDirection: 'desc',
      },
      initialColumns: [
        'Title',
        'owners',
        'Severity',
        'Status',
        'DateIdentified',
        'TargetCloseDate',
        'tags',
      ],
      fields,
    }),
    [records, fields, type, st]
  );
};

export const useGetIssuesTabTableProps = (
  type: ParentIssueType,
  records: IssueTableFields[] | undefined
): TablePropsWithActions<IssueTableFields> => {
  const props = useGetIssuesTabProps(type, records);

  return useGetTableProps(props);
};
